import {Component, ElementRef, inject, OnDestroy, OnInit, QueryList, ViewChildren, ChangeDetectionStrategy} from '@angular/core';
import {finalize, Subscription} from 'rxjs';
import {ActivatedRoute, Router, RouterLink} from '@angular/router';
import {AuthService} from "../../services/auth/auth.service";
import {
  AbstractControl,
  FormControl,
  ReactiveFormsModule,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators
} from "@angular/forms";
import {AuthControllerService, JwtResponse, User} from "../../api";
import {StateConfigService} from "../../services/state/state-config.service";
import {ErrorService} from "../../services/error/error.service";
import {faBars} from "@fortawesome/pro-solid-svg-icons";

import {NgIf, NgOptimizedImage} from "@angular/common";
import {MatDialogClose} from "@angular/material/dialog";
import {BicButtonComponent, BicInputComponent} from "@buildinginformationcloud/bic-ui-components";
import {NotificationsService} from "../../services/notifications/notifications.service";

/**
 * Authentication Component
 */
@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [

    NgIf,
    ReactiveFormsModule,
    RouterLink,
    MatDialogClose,
    NgOptimizedImage,
    BicButtonComponent,
    BicInputComponent,
  ]
})
export class LoginComponent implements OnInit, OnDestroy {
  formBuilder = inject(UntypedFormBuilder);
  stateConfig = inject(StateConfigService);
  authService = inject(AuthService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private authControllerService = inject(AuthControllerService);
  private errorService = inject(ErrorService);
  private notificationsService = inject(NotificationsService);


  loginForm!: UntypedFormGroup;
  registerForm!: UntypedFormGroup;
  passwordForm!: UntypedFormGroup;
  resetPasswordForm!: UntypedFormGroup;
  mfaForm!: UntypedFormGroup;

  loginError: boolean = false;
  mfaError: boolean = false;
  notRegisteredError: boolean = false;
  verificationError: boolean = false;
  alreadyRegisteredError: boolean = false;
  loginLoading: boolean = false;
  registerLoading: boolean = false;
  mfaLoading: boolean = false;
  reactivateLoading: boolean = false;

  /**
   * Meldung zum Kontozustand unter dem Login- bzw. Registrierungsformular.
   *
   * <p>Vorher hingen die Meldungen allein am HTTP-Status, und ein zur Löschung vorgemerktes Konto
   * fiel in dieselben Stati wie ein unverifiziertes (403) bzw. ein doppelt registriertes (400). Es
   * las deshalb "Ihr Account wurde noch nicht verifiziert" -- sachlich falsch -- oder "Es existiert
   * bereits ein Account", was zwar stimmt, aber nicht erklärt, warum der Login trotzdem scheitert.
   * Die Meldung kommt jetzt aus dem {reason} der Antwort und nennt das Löschdatum.
   */
  accountStateMessage: string = '';


  @ViewChildren('otpInput') otpInputs!: QueryList<ElementRef>;

  isRegister: boolean = false;
  isVerify: boolean = false;
  isPasswordReset: boolean = false;
  isNewVerification: boolean = false;
  isProfile: boolean = false;
  isDeletedAccount: boolean = false;
  isMfa: boolean = false;
  isReactivate: boolean = false;
  response!: JwtResponse;


  showPassword: boolean = false;
  showPasswordRepeat: boolean = false;

  user!: User;
  successSent!: Boolean;

  private errorSubscription!: Subscription;

  /**
   * Constructor
   */
  constructor() {
    if(this.router.getCurrentNavigation()!.extras!.state){
      if(this.router.getCurrentNavigation()!.extras!.state!.response) {
        this.response = this.router.getCurrentNavigation()!.extras!.state!.response!;
      }
    }
  }

  /**
   * Init Component
   */
  ngOnInit(): void {
    // this.openDialog()
    this.loginForm = this.formBuilder.group({
      mail: new UntypedFormControl("", [Validators.required, Validators.email]),
      password: new UntypedFormControl("", [Validators.required]),
    });

    this.registerForm = this.formBuilder.group({
      mail: new UntypedFormControl("", [Validators.required, Validators.email]),
      firstName: new UntypedFormControl("", [Validators.required, this.validateName()]),
      lastName: new UntypedFormControl("", [Validators.required, this.validateName()]),
      termsAndConditions: new UntypedFormControl(false, [Validators.required]),
    });

    this.passwordForm = this.formBuilder.group({
      password: new FormControl('', [Validators.minLength(8), Validators.required]),
      passwordRepeat: new FormControl('', [Validators.required, this.matchPasswords()]),
      token: this.route.snapshot.queryParams.token
    });

    this.resetPasswordForm = this.formBuilder.group({
      mail: new UntypedFormControl("", [Validators.required, Validators.email]),
    });

    this.mfaForm = this.formBuilder.group({
      code1: ['', [Validators.required, Validators.pattern(/^\d$/)]],
      code2: ['', [Validators.required, Validators.pattern(/^\d$/)]],
      code3: ['', [Validators.required, Validators.pattern(/^\d$/)]],
      code4: ['', [Validators.required, Validators.pattern(/^\d$/)]],
      code5: ['', [Validators.required, Validators.pattern(/^\d$/)]],
      code6: ['', [Validators.required, Validators.pattern(/^\d$/)]]
    });


    this.route.queryParams.subscribe(params => {
      this.isDeletedAccount = params.isDeletedAccount;
    })

    this.route.data.subscribe(data => {
      this.isRegister = data.register ?? false;
      this.isVerify = data.verify ?? false;
      this.isPasswordReset = data.resetPassword ?? false;
      this.isNewVerification = data.newVerification ?? false;
      this.isProfile = data.profile ?? false;
      this.isMfa = data.isMfa ?? false;
      this.isReactivate = data.isReactivate ?? false;

      // Der Reaktivierungsdialog lebt vom Navigations-State (Löschdatum + Reaktivierungs-Token).
      // Wer die URL direkt aufruft oder neu lädt, hat den nicht -- dann zurück zum Login, statt
      // einen leeren Dialog zu zeigen.
      if (this.isReactivate && !this.response?.reactivationToken) {
        this.router.navigateByUrl("/login");
        return;
      }

      if(this.isVerify && !this.authService.isLoggedIn()){
        this.passwordForm = this.formBuilder.group({
          password: new FormControl('', [Validators.minLength(8), Validators.required, this.passwordValidator()]),
          passwordRepeat: new FormControl('', [Validators.required, this.matchPasswords()]),
          token: this.route.snapshot.queryParams.token
        });

        // @ts-ignore
        this.passwordForm.get('password').valueChanges.subscribe(() => {
          // @ts-ignore
          this.passwordForm.get('passwordRepeat').updateValueAndValidity();
        });
      }

      if (data.logout) {
        this.authService.logout();
        this.router.navigateByUrl("/login");
      }

      if(this.authService.isLoggedIn()){
        this.authControllerService.profile().subscribe((user) => {
          this.user = user;
          if (!this.user.street) {
            this.router.navigate(["/profile"], {queryParams: {init: true}}).then(() => {

            })
          } else {
            this.stateConfig.selectProject(undefined)
          }
        });
      }
    });

    // Subscribe to error events for backend errors (auth endpoints only)
    this.errorSubscription = this.errorService.errorEvent$.subscribe((error) => {
      if (!error.url?.includes('/auth/')) {
        return;
      }
      if (error.status == 401) {
        this.loginLoading = false;
        this.loginError = true;
        this.notificationsService.showErrorMessage('Passwort oder E-Mail inkorrekt!');
      }
      if (error.status == 404) {
        this.loginLoading = false;
        this.notRegisteredError = true;
        this.notificationsService.showErrorMessage('Zu dieser E-Mail Adresse wurde noch kein Account erstellt!');
      }
      if (error.status == 403) {
        this.loginLoading = false;
        this.verificationError = true;
        this.accountStateMessage = this.accountStateText(error.error?.reason, error.error?.deletionDate);
        this.notificationsService.showErrorMessage(this.accountStateMessage);
      }
    });
  }

  ngOnDestroy(): void {
    this.errorSubscription.unsubscribe();
  }

  login(): void {
    if (this.loginLoading) {
      return;
    }

    this.loginError = false
    this.notRegisteredError = false
    this.verificationError = false
    this.accountStateMessage = ''

    let success = false
    this.loginError = false

    this.authService.clearStorage()
    this.loginLoading = true;
    this.authService.login(
      this.loginForm.get('mail')?.value,
      this.loginForm.get('password')?.value
    ).pipe(
      finalize(() => {
        this.loginLoading = false;
      })
    ).subscribe({
      next: (next) => {
        if (next) {
          this.authControllerService.profile().subscribe((user) => {
            this.user = user;
            if (!this.user.street) {
              this.router.navigate(["/profile"], {queryParams: {init: true}}).then(() => {

              })
            } else {
              this.stateConfig.selectOrganisation(undefined, true, false, true)
            }
          });
        }
      },
      error: () => {
        this.loginLoading = false;
      }
    });
  }

  validateName(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      return control.value.trim().length >= 2 ? null : { 'invalidName': true };
    };
  }

  register(): void {
    if (this.registerLoading) {
      return;
    }

    this.alreadyRegisteredError = false;
    this.accountStateMessage = '';
    this.registerForm.value.firstName = this.registerForm.value.firstName.trim();
    this.registerForm.value.lastName = this.registerForm.value.lastName.trim();

    if(this.registerForm.valid && this.registerForm.get('termsAndConditions')?.value == true){
      this.registerLoading = true;
      this.authControllerService.register({
        user: this.registerForm.value
      }).pipe(
        finalize(() => {
          this.registerLoading = false;
        })
      ).subscribe({
        next: () => {
          this.notificationsService.showSuccessMessage("Registrierung erfolgreich.\nBitte verifizieren Sie Ihren Account über die Ihnen zugesandte E-Mail. Sie sollten diese innerhalb weniger Minuten erhalten.");
          this.router.navigateByUrl("/login");
        },
        error: (err) => {
          this.alreadyRegisteredError = true;
          this.accountStateMessage = this.accountStateText(err.error?.reason, err.error?.deletionDate, true);
          this.notificationsService.showErrorMessage("Registrierung fehlgeschlagen.\n" + this.accountStateMessage)
        },
      });
    }
  }


  /**
   * Übersetzt den {reason} einer Auth-Antwort in die Meldung, die die Nutzerin liest.
   *
   * <p>Jeder Zustand nennt sich selbst UND den Weg nach vorn: vorgemerkt -> anmelden und
   * reaktivieren, Frist abgelaufen -> Support, unverifiziert -> Bestätigungs-Mail. Ohne bekannten
   * Grund (leerer Body, älteres Backend) bleibt es bei der bisherigen Formulierung.
   */
  private accountStateText(reason?: string, deletionDate?: string | Date, forRegistration: boolean = false): string {
    const date = this.formatDeletionDate(deletionDate);

    switch (reason) {
      case 'ACCOUNT_PENDING_DELETION':
        return date
          ? `Dieser Account wurde zur Löschung vorgemerkt und wird am ${date} endgültig entfernt. Melden Sie sich bis dahin mit Ihren bisherigen Zugangsdaten an, um ihn zu reaktivieren.`
          : 'Dieser Account wurde zur Löschung vorgemerkt. Melden Sie sich innerhalb der Widerrufsfrist mit Ihren bisherigen Zugangsdaten an, um ihn zu reaktivieren.';
      case 'ACCOUNT_DELETED':
        return date
          ? `Dieser Account wurde gelöscht. Die Frist zum Widerruf ist am ${date} abgelaufen. Bitte wenden Sie sich an support@thinkbic.de.`
          : 'Dieser Account wurde gelöscht und die Frist zum Widerruf ist abgelaufen. Bitte wenden Sie sich an support@thinkbic.de.';
      case 'ACCOUNT_NOT_VERIFIED':
        return 'Ihr Account wurde noch nicht verifiziert. Bitte bestätigen Sie den Link in Ihrer Registrierungs-E-Mail.';
      case 'ACCOUNT_ALREADY_EXISTS':
        return 'Es existiert bereits ein Account mit dieser E-Mail Adresse!';
      default:
        return forRegistration
          ? 'Unter dieser E-Mail Adresse ist bereits ein Account vorhanden.'
          : 'Ihr Account wurde noch nicht verifiziert!';
    }
  }

  /** Löschdatum als dd.MM.yyyy, oder Leerstring, wenn keins mitgeliefert wurde. */
  private formatDeletionDate(value?: string | Date): string {
    if (!value) {
      return '';
    }
    const date = new Date(value);
    return isNaN(date.getTime())
      ? ''
      : date.toLocaleDateString('de-DE', {day: '2-digit', month: '2-digit', year: 'numeric'});
  }

  /** Löschdatum für den Reaktivierungsdialog. */
  get deletionDateText(): string {
    return this.formatDeletionDate(this.response?.deletionDate);
  }

  /**
   * "Konto reaktivieren": nimmt die Löschung zurück und meldet in einem Zug an.
   *
   * <p>Hierher kommt nur, wer Passwort und ggf. 2FA bereits bestanden hat -- die Anmeldung selbst
   * hat kein Access-Token ausgeliefert, sondern nur das Reaktivierungs-Token, das dieser Aufruf
   * einlöst.
   */
  reactivate(): void {
    if (this.reactivateLoading) {
      return;
    }
    this.reactivateLoading = true;

    this.authService.reactivateAccount(this.response.reactivationToken!).pipe(
      finalize(() => {
        this.reactivateLoading = false;
      })
    ).subscribe({
      next: () => {
        this.notificationsService.showSuccessMessage("Konto reaktiviert.\nDie Löschung wurde zurückgenommen, Ihre Daten sind unverändert vorhanden.");
        this.authControllerService.profile().subscribe((user) => {
          this.user = user;
          if (!this.user.street) {
            this.router.navigate(["/profile"], {queryParams: {init: true}}).then(() => {
            })
          } else {
            this.stateConfig.selectOrganisation(undefined, true, false, true)
          }
        });
      },
      error: () => {
        this.reactivateLoading = false;
      }
    });
  }

  /**
   * Zurück zum Login aus dem Abschieds-Panel. Der Flag-Reset muss mit, sonst bleibt das Panel
   * stehen: Ziel ist dieselbe Route, nur ohne den Query-Parameter.
   */
  backToLogin(): void {
    this.isDeletedAccount = false;
    this.router.navigateByUrl("/login");
  }

  /**
   * "Löschung beibehalten": rührt das Konto nicht an. Die Frist läuft weiter, und bis sie abläuft
   * ist derselbe Weg jederzeit wieder offen.
   */
  keepDeletion(): void {
    this.authService.clearStorage();
    this.router.navigate(["/login"], {queryParams: {isDeletedAccount: true}});
  }

  matchPasswords(): ValidatorFn {

    return (control: AbstractControl): ValidationErrors | null => {
      if (this.passwordForm) {
        return this.passwordForm.get('password')?.value == control.value ? null : {matchValue: false};
      } else {
        return {matchValue: false};
      }

    };
  }

  passwordValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      if (!value) {
        return null;
      }

      const hasUpperCase = /[A-Z]+/.test(value);
      const hasLowerCase = /[a-z]+/.test(value);
      const hasNumeric = /[0-9]+/.test(value);
      const hasSpecialCharacter = /[!\?&%=.\-:*#@$\\]+/.test(value);
      const hasNoSpaces = !/\s/.test(value);


      const passwordValid = hasUpperCase && hasLowerCase && hasNumeric && hasSpecialCharacter && hasNoSpaces;

      return !passwordValid ? {passwordInvalid: true} : null;
    };
  }

  testLength(str: string): boolean {
    return str.length >= 8;
  }

  testLowerCase(str: string): boolean {
    return /[a-z]+/.test(str);
  }

  testUpperCase(str: string): boolean {
    return /[A-Z]+/.test(str);
  }

  testNumeric(str: string): boolean {
    return /[0-9]+/.test(str);
  }

  testSpecialCharacter(str: string): boolean {
    return /[!\?&%=.\-:*#@$\\]+/.test(str);
  }


  setPassword(): void {
    if(this.passwordForm.get('password')?.errors == null &&  this.passwordForm.get('passwordRepeat')?.errors == null){
      this.authControllerService.resetPassword(this.passwordForm.value).subscribe(() => {
        this.router.navigateByUrl("/login");
      })
    }
  }

  resetPassword(): void {
    this.errorService.errorEvent$.subscribe((error) => {
      if (error.status == 404) {
        this.notRegisteredError = true;
      }
    });
    this.notRegisteredError = false;
    this.resetPasswordForm.get('mail')?.markAsTouched();
    if(this.resetPasswordForm.get('mail')?.errors === null){
      this.authControllerService.resetPasswordRequest(this.resetPasswordForm.get("mail")?.value).subscribe(() => {
        this.notificationsService.showSuccessMessage("Passwort erfolgreich zurückgesetzt.\nBitte wählen Sie ein neues Passwort über die Ihnen zugesandte E-Mail. Sie sollten diese innerhalb weniger Minuten erhalten.\nSie können dieses Fenster nun schließen")
      });
    }
  }

  verifyMfa(): void {
    this.errorService.errorEvent$.subscribe((error) => {
      if (error.status == 401) {
        this.mfaError = true;
        this.mfaLoading = false;
      }
    });
    this.mfaError = false;
    this.mfaLoading = true;
    const code = '' + this.mfaForm.get('code1')?.value + this.mfaForm.get('code2')?.value + this.mfaForm.get('code3')?.value + this.mfaForm.get('code4')?.value + this.mfaForm.get('code5')?.value + this.mfaForm.get('code6')?.value;
    this.authService.loginMfa(this.response!.mail! ?? '', '', code).subscribe({
      next: () => {
        this.mfaLoading = false;
        this.stateConfig.selectOrganisation(undefined, true, false, true);
      },
      error: () => {
        this.mfaLoading = false;
      }
    });


  }


  moveToNext(event: any, index: number): void {
    const input = event.target;
    const key = event.key;

    if (input.value.length > 1) {
      input.value = input.value.slice(0, 1);
    }


    if (event.type === 'paste') {
      const pasteData = (event as ClipboardEvent).clipboardData?.getData('text');

      const textWithoutSpaces = pasteData!.replace(/\s+/g, '');
      if (textWithoutSpaces && textWithoutSpaces.length === 6) {
        for (let i = 0; i < 6; i++) {
          this.mfaForm.controls[`code${i + 1}`].setValue(textWithoutSpaces[i]);
        }
        this.otpInputs.toArray()[5].nativeElement.focus();
        event.preventDefault();
      }
      return;
    }

    if (key === 'Backspace' && input.value.length === 0 && index > 0) {
      this.otpInputs.toArray()[index - 1].nativeElement.focus();
    } else if (input.value.length === 1 && index < 5) {
      this.otpInputs.toArray()[index + 1].nativeElement.focus();
    }
  }

  resendMail(): void {
    if(this.response && this.response?.mail){
      this.authControllerService.resendCode(this.response!.mail! ?? '').subscribe(() => {
        if(this.response.isMailMfa){
          this.notificationsService.showSuccessMessage("Code erfolgreich erneut angefordert.\nIhnen wurde eine E-Mail mit einem neuen Code per Mail zugesendet. Sie sollten diese innerhalb weniger Minuten erhalten.")
        }else{
          this.notificationsService.showSuccessMessage("Code per E-Mail angefordert.\nDa Sie keinen Zugriff mehr auf den Code innerhalb Ihrer Authentifikations-App haben, wurde Ihnen ein Code per E-Mail zugesandt." +
            " \nSie sollten diese innerhalb weniger Minuten erhalten.")
        }
      });
    }

  }


  protected readonly window = window;
  protected readonly faBars = faBars;
}
